import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericResponse } from '@app/core/models/generic-response';
import { SESSION_STATUS, normalizeSessionStatus } from '@app/core/constants/core.constants';
import { LoggingService, TimezoneConverterService } from '@app/core/services';
import { AuthStore } from '@app/core/state/auth.store';
import { FileItem, UploadSessionItem } from '@modules/reconciliation-manager/models';
import { environment } from 'environments/environment';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

type UploadSessionResponseItem = UploadSessionItem & {
    totalFiles?: number;
};

@Injectable({
    providedIn: 'root',
})
export class GetSessionService {
    constructor(
        private http: HttpClient,
        private loggingService: LoggingService,
        private authStore: AuthStore,
        private timezoneConverterService: TimezoneConverterService
    ) {}

    getUploadSessions(): Observable<UploadSessionItem[]> {
        // Retrieve client and user values from the global state.
        const clientIdState = this.authStore.getClientValue();
        const userState = this.authStore.getUserValue();

        // Build headers for the API call.
        const headers = new HttpHeaders({
            'X-ClientId': clientIdState ?? '',
            'X-User': userState ?? '',
        });

        return this.http
            .get<GenericResponse<UploadSessionResponseItem[]>>(
                `${environment.apiRoot}sessions`,
                { headers }
            )
            .pipe(
                map((response): UploadSessionItem[] => {
                    const rawSessions: UploadSessionResponseItem[] = response.data || [];

                    return rawSessions.map((session) => {
                        const results = (session as any).results ?? session.filename ?? '';
                        const createdDateUtc = session.createdDateUtc || session.createdDate;
                        const lastUpdatedAt = session.lastUpdatedAt || undefined;
                        const completeDateUtc = session.completeDateUtc || session.completeDate || undefined;

                        const displayCreatedDate = this.formatSessionDisplayDate(
                            createdDateUtc || session.createdDate
                        );

                        let displayCompleteDate = '';
                        if (lastUpdatedAt) {
                            displayCompleteDate = this.formatSessionDisplayDate(lastUpdatedAt);
                        } else if (completeDateUtc) {
                            displayCompleteDate = this.formatSessionDisplayDate(completeDateUtc);
                        }

                        const totalValue = session.totalFiles ?? session.total ?? 0;

                        return {
                            ...session,
                            results,
                            filename: session.filename ?? results ?? '',
                            total: Number(totalValue),
                            createdDateUtc,
                            lastUpdatedAt,
                            completeDateUtc,
                            createdDate: displayCreatedDate,
                            completeDate: displayCompleteDate ?? '',
                        } as UploadSessionItem;
                    });
                }),
                switchMap((sessions) => this.enrichSingleOpenSessionFileNames(sessions, headers)),
                map((sessions) => {
                    const sortedSessions = sessions.sort(
                        (a, b) =>
                            this.getSessionSortTimestamp(b) - this.getSessionSortTimestamp(a)
                    );
                    this.loggingService.info('Sorted upload sessions:', sortedSessions);
                    return sortedSessions;
                }),
                catchError((error) => {
                    this.loggingService.error('Error fetching upload sessions:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Run Reconciliation listing requires the underlying file name for
     * OPEN single-file sessions. The sessions endpoint does not always include
     * it, so we resolve it from the session file list only for those rows.
     */
    private enrichSingleOpenSessionFileNames(
        sessions: UploadSessionItem[],
        headers: HttpHeaders
    ): Observable<UploadSessionItem[]> {
        const targets = sessions.filter((session) => this.requiresSingleFileNameLookup(session));
        if (!targets.length) {
            return of(sessions);
        }

        return forkJoin(
            targets.map((session) =>
                this.getSingleSessionFileName(session.id, headers).pipe(
                    map((fileName) => ({ sessionId: session.id, fileName }))
                )
            )
        ).pipe(
            map((resolved) => {
                const bySessionId = new Map(resolved.map((item) => [item.sessionId, item.fileName]));
                return sessions.map((session) => {
                    const fileName = bySessionId.get(session.id);
                    return fileName
                        ? { ...session, filename: fileName, results: fileName }
                        : session;
                });
            })
        );
    }

    private requiresSingleFileNameLookup(session: UploadSessionItem): boolean {
        const totalFiles = Number(session?.totalFiles ?? session?.total ?? 0);
        const normalizedStatus = normalizeSessionStatus(session?.status);
        const knownFileName = session?.filename?.trim() || session?.results?.trim() || '';

        return totalFiles === 1 && normalizedStatus === SESSION_STATUS.OPEN && !knownFileName;
    }

    private getSingleSessionFileName(
        sessionId: string,
        headers: HttpHeaders
    ): Observable<string | null> {
        const params = new HttpParams().set('status', '1');

        return this.http
            .get<GenericResponse<FileItem[] | { files?: FileItem[] | null }>>(
                `${environment.apiRoot}sessions/${encodeURIComponent(sessionId)}/files`,
                { headers, params }
            )
            .pipe(
                map((response) => {
                    const payload = response?.data;
                    const files = Array.isArray(payload)
                        ? payload
                        : Array.isArray(payload?.files)
                          ? payload.files
                          : [];

                    return files.length === 1 ? files[0]?.filename?.trim() || null : null;
                }),
                catchError((error) => {
                    this.loggingService.warn(
                        '[GetSessionService] Unable to resolve file name for OPEN single-file session.',
                        { sessionId, error }
                    );
                    return of(null);
                })
            );
    }

    /**
     * Upload Sessions screen should reflect the operator's browser clock.
     * Format canonical UTC timestamps using the browser timezone so Created Date
     * and Last Updated Date remain consistent on the same grid.
     */
    private formatSessionDisplayDate(dateValue?: string): string {
        if (!dateValue) {
            return '';
        }

        const browserZone = this.timezoneConverterService.getBrowserTimeZone() || undefined;
        return this.timezoneConverterService.formatUtcToLocal(dateValue, browserZone);
    }

    /**
     * Sort sessions by the latest meaningful activity timestamp.
     *
     * Priority:
     * 1. `lastUpdatedAt` from the backend
     * 2. legacy completion timestamp fields
     * 3. session creation timestamp
     *
     * This keeps newly updated sessions visible at the top while preserving the
     * existing created-date fallback for older payloads.
     */
    private getSessionSortTimestamp(session: UploadSessionItem): number {
        const candidateValues = [
            session.lastUpdatedAt,
            session.completeDateUtc,
            session.createdDateUtc,
            session.completeDate,
            session.createdDate,
        ];

        for (const value of candidateValues) {
            if (!value) {
                continue;
            }

            const timestamp = new Date(value).getTime();
            if (!Number.isNaN(timestamp)) {
                return timestamp;
            }
        }

        return 0;
    }

    getGetSession$(): Observable<{}> {
        return of({});
    }
}
